// =====================================================================
// commande-prete/parametres.js — Commandes prêtes v2 (16-10-2026) — PARAMÈTRES CLIENT
// =====================================================================
// Formulaire de CommandesPretesClients.jsx (SPEC-v2 §2.1, §3 « Paramètres client »,
// §4 modèles) : client (catalogue UmClient existant, complété) → réglages du mail
// « commande prête » et du mail « chargée ». Le serveur valide aussi ; l'écran
// prévient AVANT, avec un message humain, et bloque l'enregistrement d'un réglage
// impossible (aucune langue, néerlandais sans modèle, adresse invalide…).
// Fonctions PURES (aucun DOM) : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

import { lireLangues, normClient, NOMS_LANGUES } from './clients.js';
import {
  MAX_FRANCHISE_JOURS, MAX_PRIX_CENTIMES, MSG_STOCKAGE_FRANCHISE, MSG_STOCKAGE_PRIX, MSG_STOCKAGE_DEPUIS, MSG_STOCKAGE_PROJET,
  MSG_STOCKAGE_PROJET_CLIENT, msgStockageMoisClos, moisAVerifier,
  prixEnCentimes, centimesEnTexte, joursEnEntier, jourValide, bornesDepuis, texteJour,
} from './stockage.js';
import { aujourdhuiBruxelles } from '../../lib/dates.js';

export const LANGUES = ['en', 'fr', 'nl'];
// Langues qui ont un modèle par défaut (= le mail actuel, modèle de Sandrine) ; nl n'en a pas.
export const LANGUES_AVEC_DEFAUT = ['en', 'fr'];
// Repères du mail « commande prête » (§4) et du mail « chargée » ; un repère inconnu reste tel quel
// dans le mail (mêmes listes que le serveur : ready-order.rules.js REPERES_PRETE / REPERES_CHARGE).
export const REPERES = ['ref', 'unites', 'tableau', 'photos', 'adresse'];
export const REPERES_CHARGE = ['ref', 'date', 'charge', 'quai'];
export const LANGUES_DEFAUT = 'en,fr';
// Bornes du serveur (lireReglages / lireModeles / lireEmails).
export const MAX_NOM = 100;
export const MAX_TRANSPORTEUR = 20;
export const MAX_SUJET = 200;
export const MAX_CORPS = 10000;
export const MAX_ADRESSES = 20;

export const MSG_AUCUNE_LANGUE = 'Choisissez au moins une langue : sans langue, aucun mail ne peut partir.';
export const MSG_NL_SANS_MODELE = 'Néerlandais : pas de modèle par défaut — écrivez le sujet et le texte du mail « commande prête ».';
export const MSG_NL_SANS_MODELE_CHARGE = 'Néerlandais : écrivez aussi le sujet et le texte du mail « chargée ».';
export const MSG_SANS_DESTINATAIRE = 'Destinataires (À) : indiquez au moins une adresse — sans destinataire, le mail « commande prête » ne peut pas partir.';
export const MSG_SANS_DESTINATAIRE_CHARGE = 'Mail au chargement activé : indiquez au moins un destinataire (À).';
export const MSG_CHARGE_SANS_PROPOSE = 'Mail au chargement : seulement pour un client proposé dans « Commandes prêtes » (sinon il ne se charge jamais).';
// v3 (A2) — facturation automatique
export const MSG_PROJET_REQUIS = 'Automatisation active (filmage + palettes) : choisissez le projet de facturation.';
export const MSG_PROJET_INACTIF = 'Projet de facturation inactif ou introuvable : choisissez un projet actif dans la liste.';
// Revue 16/10 : un client inactif garde son réglage « Proposé » (rien n'est modifié en silence) — dit à l'écran.
export const MSG_CLIENT_INACTIF = 'Client inactif dans Dimensions : il n\'est proposé nulle part tant qu\'il est inactif.';
export const MSG_NOM_MANQUANT = 'Nom du client obligatoire.';

const NOM_LANGUE_MAJ = { en: 'Anglais', fr: 'Français', nl: 'Néerlandais' };
export const nomLangue = (l) => NOM_LANGUE_MAJ[l] || l;

// ---------- Adresses ----------
const EMAIL = /^[^\s@,;<>()"]+@[^\s@,;<>()"]+\.[^\s@,;<>()".]{2,}$/;
export const emailValide = (e) => { const s = String(e ?? '').trim(); return s.length <= 254 && EMAIL.test(s); };   // (254 : comme le serveur)
// `v` : le texte enregistré, ou les lignes du formulaire (une adresse par ligne).
export const decouperEmails = (v) => (Array.isArray(v) ? v.join(',') : String(v ?? '')).split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
// Une adresse en double (casse ignorée, comme le serveur) ne compte qu'une fois.
const uniques = (l) => { const vues = new Set(); return l.filter((e) => { const c = e.toLowerCase(); if (vues.has(c)) return false; vues.add(c); return true; }); };
// Forme enregistrée : adresses séparées par des virgules (comme `emails` de UmClient).
export const normaliserEmails = (v) => uniques(decouperEmails(v)).join(', ');

// ---------- Une adresse par ligne (29-10-2026, demande Silviu : « ajouter plusieurs personnes en CC et en Réponse à ») ----------
// Le formulaire garde des LIGNES (au moins une, vide pour taper la première) ; le serveur reçoit toujours « a, b ».
export const lignesAdresses = (v) => { const l = decouperEmails(v); return l.length ? l : ['']; };
export const adresseFausse = (ligne) => decouperEmails(ligne).some((e) => !emailValide(e));
// Adresses que le serveur accepte telles quelles (toutes valides, MAX_ADRESSES au plus) — un champ vide aussi.
export const adressesAcceptees = (lignes) => { const l = decouperEmails(lignes); return l.every(emailValide) && uniques(l).length <= MAX_ADRESSES; };
// Champ en erreur : ses lignes fausses — ou toutes, si l'erreur porte sur le champ entier (aucune adresse, trop d'adresses).
export const lignesEnErreur = (lignes) => { const f = lignes.map(adresseFausse); return f.some(Boolean) ? f : f.map(() => true); };
export const ligneAdresseMaj = (form, champ, i, v) => ({ ...form, [champ]: form[champ].map((x, j) => (j === i ? v : x)) });
// « ＋ Ajouter » : MAX_ADRESSES adresses au plus (les adresses valides, comptées une fois) — et jamais une 2e ligne
// vide : celle qui attend reçoit le curseur (écran).
export const maxAdressesAtteint = (lignes) => uniques(decouperEmails(lignes).filter(emailValide)).length >= MAX_ADRESSES;
export const ligneAdresseAjoutee = (form, champ) => (form[champ].some((x) => !String(x).trim()) || maxAdressesAtteint(form[champ])
  ? form : { ...form, [champ]: [...form[champ], ''] });
// La dernière ligne se vide, elle ne disparaît pas (il faut toujours un champ où taper).
export const ligneAdresseRetiree = (form, champ, i) => ({ ...form, [champ]: form[champ].length > 1 ? form[champ].filter((_, j) => j !== i) : [''] });
// Texte collé → ses adresses, même règle que la validation (apostrophe comprise) : « Nom, Prénom <adresse> » ou
// « adresse (Nom) » copiés d'Outlook / Gmail → les adresses seules ; un morceau avec « @ » qui n'est pas une adresse
// (faute de frappe) est gardé — il sera signalé en rouge, jamais perdu en silence ; les noms, eux, sont laissés.
const ADRESSE_DANS_TEXTE = /[^\s@,;<>()"]+@[^\s@,;<>()"]+\.[^\s@,;<>()".]{2,}/g;
export function adressesCollees(texte) {
  // « mailto:a@x.be?subject=… » (lien copié) : l'adresse seule — ni le préfixe, ni les paramètres
  const s = String(texte ?? '').replace(/mailto:([^?\s,;<>"]*)(\?[^\s,;<>"]*)?/gi, '$1');
  const restes = s.replace(ADRESSE_DANS_TEXTE, ' ').split(/[\s,;<>()"]+/).filter((x) => x.includes('@'));
  // « 'a@x.be' » (Outlook classique) : sans guillemets ; chacune une fois
  const nettes = [...(s.match(ADRESSE_DANS_TEXTE) || []), ...restes].map((a) => a.replace(/^['"]+|['"]+$/g, ''));
  return uniques(nettes.filter(Boolean));
}
// La ligne i devient plusieurs lignes, À SA PLACE (son texte restant, puis les adresses collées) — les lignes au-dessus
// ne bougent pas (le curseur reste sur la sienne).
export const lignesCollees = (form, champ, i, adresses) => ({
  ...form, [champ]: [...form[champ].slice(0, i), ...(adresses.length ? adresses : ['']), ...form[champ].slice(i + 1)],
});

// ---------- Modèles { en: { subject, body }, … } (texte JSON en base, ou objet) ----------
export function lireModeles(v) {
  let o = v;
  if (typeof v === 'string') {
    try { o = v.trim() ? JSON.parse(v) : {}; } catch { o = {}; }
  }
  const res = {};
  if (!o || typeof o !== 'object' || Array.isArray(o)) return res;
  for (const l of LANGUES) {
    const m = o[l];
    if (m && typeof m === 'object') res[l] = { subject: String(m.subject ?? ''), body: String(m.body ?? '') };
  }
  return res;
}
const vide = (s) => !String(s ?? '').trim();
export const modeleComplet = (m) => !!m && !vide(m.subject) && !vide(m.body);
// Commencé mais incomplet (sujet sans texte, ou l'inverse).
export const modeleEntame = (m) => !!m && !modeleComplet(m) && !(vide(m.subject) && vide(m.body));
// Modèles à enregistrer : seulement les modèles COMPLETS (vide = modèle par défaut). Un modèle entamé
// d'une langue cochée bloque l'enregistrement (validerForm) ; celui d'une langue décochée n'est pas envoyé.
export function modelesAEnregistrer(modeles) {
  const res = {};
  for (const l of LANGUES) {
    const m = modeles && modeles[l];
    if (!modeleComplet(m)) continue;
    res[l] = { subject: String(m.subject).trim(), body: String(m.body) };
  }
  return res;
}

// Repères {xxx} d'un texte qui ne sont pas dans la liste connue (même motif que le serveur).
export function reperesInconnus(texte, connus = REPERES) {
  const vus = new Set();
  for (const m of String(texte ?? '').matchAll(/\{([A-Za-z_]+)\}/g)) if (!connus.includes(m[1])) vus.add(m[0]);
  return [...vus];
}
// Modèle d'une langue : vide (= défaut) ou sujet ET texte ; jamais l'un sans l'autre (le serveur refuse).
export function verifModele(m, l, quoi) {
  if (!m || (vide(m.subject) && vide(m.body))) return null;
  const nom = `${nomLangue(l)} (${quoi})`;
  if (vide(m.subject) || vide(m.body)) return `${nom} : remplissez le sujet ET le texte — ou laissez les deux vides${LANGUES_AVEC_DEFAUT.includes(l) ? ' pour le modèle par défaut' : ''}.`;
  if (String(m.subject).trim().length > MAX_SUJET) return `${nom} : sujet de ${MAX_SUJET} caractères au plus.`;
  if (/[\r\n]/.test(String(m.subject))) return `${nom} : le sujet tient sur une seule ligne.`;
  if (String(m.body).length > MAX_CORPS) return `${nom} : texte de ${MAX_CORPS} caractères au plus.`;
  return null;
}

// ---------- Formulaire ⇄ client ----------
// langues : les 3 langues dans l'ordre d'affichage (cochées d'abord, dans l'ordre du mail).
// `normaliser` (défaut) : les réglages hérités qui s'annulent sont corrigés à l'ouverture (un client
// non proposé n'a pas de mail au chargement) — l'écran montre ce qui sera enregistré, et le dit.
// (Revue 16/10 : « inactif + proposé » n'est PAS une contradiction — « Proposé » reste tel quel.) `normaliser: false` = tel que stocké (référence pour détecter une modification :
// la correction compte comme une modification, donc s'enregistre).
export function formDepuisClient(c = {}, { normaliser = true } = {}) {
  const coches = lireLangues(c.mailLanguages ?? LANGUES_DEFAUT);
  const ordre = [...coches, ...LANGUES.filter((l) => !coches.includes(l))];
  const actif = c.isActive !== false;
  const propose = !!c.readyEnabled;
  return {
    id: c.id || null,
    name: String(c.name ?? ''),
    isActive: actif,
    readyEnabled: propose,
    carrierCode: String(c.carrierCode ?? ''),
    readyTo: lignesAdresses(c.readyTo),
    readyCc: lignesAdresses(c.readyCc),
    readyReplyTo: lignesAdresses(c.readyReplyTo),
    langues: ordre.map((l) => ({ l, on: coches.includes(l) })),
    modeles: lireModeles(c.mailTemplates),
    loadMailEnabled: normaliser ? propose && !!c.loadMailEnabled : !!c.loadMailEnabled,
    modelesCharge: lireModeles(c.loadMailTemplates),
    // v3 (A2) : projet de facturation + « Automatisation active (filmage + palettes) »
    projectId: String(c.projectId ?? ''),
    autoFacturation: !!c.autoFacturation,
    // Stockage sur dalle (22-10-2026) : case cochée = les trois réglages enregistrés ; décochée, les champs
    // gardent ce qui était tapé (recocher le retrouve) mais rien ne part au serveur.
    stockageActif: c.stockageFranchiseJours != null && c.stockagePrixJourCentimes != null && !!c.stockageDepuis,
    stockageFranchise: c.stockageFranchiseJours == null ? '' : String(c.stockageFranchiseJours),
    stockagePrix: c.stockagePrixJourCentimes == null ? '' : centimesEnTexte(c.stockagePrixJourCentimes),
    stockageDepuis: String(c.stockageDepuis ?? ''),
    stockageDepuisEnregistre: String(c.stockageDepuis ?? ''),   // (une date déjà enregistrée n'est plus bornée)
  };
}
export const languesCochees = (form) => form.langues.filter((x) => x.on).map((x) => x.l);
// Réglage qui s'annule : mail au chargement pour un client non proposé (il ne se charge jamais).
export const reglageIncoherent = (f) => !!f && !!f.loadMailEnabled && !f.readyEnabled;

// Coche / décoche une langue (l'ordre est gardé).
export const basculerLangue = (form, l) => ({ ...form, langues: form.langues.map((x) => (x.l === l ? { ...x, on: !x.on } : x)) });
// Monte / descend une langue dans l'ordre du mail.
export function deplacerLangue(form, l, delta) {
  const t = [...form.langues];
  const i = t.findIndex((x) => x.l === l);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= t.length) return form;
  [t[i], t[j]] = [t[j], t[i]];
  return { ...form, langues: t };
}

// Corps PUT / POST. `formes` : forme reçue du serveur pour les langues ('string' | 'array') et les
// modèles ('string' = texte JSON | 'object') — renvoyés sous la même forme.
export function corpsDepuisForm(form, formes = {}) {
  const langues = languesCochees(form);
  const enModeles = (m) => (formes.modeles === 'string' ? JSON.stringify(modelesAEnregistrer(m)) : modelesAEnregistrer(m));
  return {
    name: form.name.trim(),
    isActive: !!form.isActive,
    readyEnabled: !!form.readyEnabled,
    carrierCode: form.carrierCode.trim() || null,
    readyTo: normaliserEmails(form.readyTo),
    readyCc: normaliserEmails(form.readyCc),
    readyReplyTo: normaliserEmails(form.readyReplyTo),
    mailLanguages: formes.langues === 'array' ? langues : langues.join(','),
    mailTemplates: enModeles(form.modeles),
    loadMailEnabled: !!form.loadMailEnabled,
    // Mail « chargée » désactivé : ses modèles ne partent pas (le serveur garde ceux qu'il a).
    ...(form.loadMailEnabled ? { loadMailTemplates: enModeles(form.modelesCharge) } : {}),
    projectId: String(form.projectId ?? '').trim() || null,
    autoFacturation: !!form.autoFacturation,
    ...corpsStockage(form),
  };
}
// Stockage sur dalle : case décochée = les trois vides (plus facturé). Valeur illisible gardée en texte (la
// validation bloque l'enregistrement ; ici, elle sert seulement à voir que le formulaire a changé).
function corpsStockage(form) {
  if (!form.stockageActif) return { stockageFranchiseJours: null, stockagePrixJourCentimes: null, stockageDepuis: null };
  const f = joursEnEntier(form.stockageFranchise);
  const c = prixEnCentimes(form.stockagePrix);
  return {
    stockageFranchiseJours: Number.isNaN(f) ? String(form.stockageFranchise) : f,
    stockagePrixJourCentimes: Number.isNaN(c) ? String(form.stockagePrix) : c,
    stockageDepuis: String(form.stockageDepuis ?? '').trim() || null,
  };
}
// Forme des champs telle que le serveur l'envoie (premier client de la liste qui en a).
export function formesDepuis(clients) {
  const formes = { langues: 'string', modeles: 'string' };
  const c = (clients || []).find((x) => x && x.mailLanguages !== undefined);
  if (c && Array.isArray(c.mailLanguages)) formes.langues = 'array';
  const m = (clients || []).find((x) => x && x.mailTemplates !== undefined && x.mailTemplates !== null);
  if (m && typeof m.mailTemplates === 'object') formes.modeles = 'object';
  return formes;
}
// Modifié = ce qui partirait au serveur a changé, ou un modèle a été commencé (à finir ou à vider).
// (Adresses : aussi les lignes telles que tapées — une adresse en double ajoutée s'enregistre une fois, ou s'annule ;
// une ligne vide, elle, n'est pas une modification.)
const lignesTapees = (f) => ['readyTo', 'readyCc', 'readyReplyTo'].map((k) => decouperEmails(f[k]).join(','));
export const formModifie = (form, initial) => JSON.stringify(corpsDepuisForm(form)) !== JSON.stringify(corpsDepuisForm(initial))
  || lignesTapees(form).join('|') !== lignesTapees(initial).join('|')
  || LANGUES.some((l) => modeleEntame(form.modeles[l]) || modeleEntame(form.modelesCharge[l]));

// ---------- Validation (avant enregistrement) ----------
// Mêmes règles que le serveur (lireReglages) : ce que l'écran accepte, le serveur l'accepte.
// `autres` : les autres clients (un nom égal à la casse / aux accents près est refusé) ; `projets` : les
// projets actifs [{ id, name, kind }] (null = liste pas reçue : le serveur vérifie) ; `moisVerrouilles` : Set des
// mois clôturés « YYYY-MM » (null = pas reçus : le serveur vérifie).
// { erreurs: [{ champ, msg }] (bloquantes, une par champ), avertissements: [texte] }
export function validerForm(form, autres = [], projets = null, aujourdhui = aujourdhuiBruxelles(), moisVerrouilles = null) {
  const erreurs = [], avertissements = [];
  const err = (champ, msg) => { if (!erreurs.some((e) => e.champ === champ)) erreurs.push({ champ, msg }); };
  const nom = form.name.trim().replace(/\s+/g, ' ');
  if (!nom) err('name', MSG_NOM_MANQUANT);
  else if (nom.length > MAX_NOM) err('name', `Nom du client : ${MAX_NOM} caractères au plus.`);
  else if (/[\u0000-\u001f\u007f]/.test(nom)) err('name', 'Nom du client : caractère invisible — retapez-le.');
  else if (nom.includes(' — ')) err('name', 'Nom du client : « — » entre deux espaces est réservé (notes des mesures) — utilisez « - ».');
  else {
    const n = normClient(nom);
    const meme = (autres || []).find((c) => c && c.id !== form.id && normClient(c.name) === n);
    if (meme) err('name', `Un client porte déjà ce nom : ${meme.name}.`);
  }
  const transp = form.carrierCode.trim();
  if (transp.length > MAX_TRANSPORTEUR || transp.includes(';')) err('carrierCode', `Transporteur : ${MAX_TRANSPORTEUR} caractères au plus, sans « ; ».`);
  for (const [champ, libelle] of [['readyTo', 'Destinataires (À)'], ['readyCc', 'Copie (CC)'], ['readyReplyTo', 'Réponse à']]) {
    const liste = decouperEmails(form[champ]);
    const u = uniques(liste);
    const fausses = liste.filter((e) => !emailValide(e));
    if (fausses.length) err(champ, `${libelle} : adresse${fausses.length > 1 ? 's' : ''} invalide${fausses.length > 1 ? 's' : ''} ${fausses.map((e) => `« ${e} »`).join(', ')}`);
    else if (u.length > MAX_ADRESSES) err(champ, `${libelle} : ${MAX_ADRESSES} adresses au plus.`);
    if (u.length < liste.length) avertissements.push(`${libelle} : adresse en double — retirez la ligne en trop (une seule sera enregistrée).`);
  }
  const destinataires = decouperEmails(form.readyTo).length > 0;
  // Proposé dans « Commandes prêtes » (même inactif : le réglage reste) → il faut un destinataire.
  if (form.readyEnabled && !destinataires) err('readyTo', MSG_SANS_DESTINATAIRE);
  if (form.loadMailEnabled && !destinataires) err('readyTo', MSG_SANS_DESTINATAIRE_CHARGE);
  if (form.loadMailEnabled && !form.readyEnabled) err('loadMailEnabled', MSG_CHARGE_SANS_PROPOSE);
  // v3 (A2) : l'automatisation exige un projet de facturation actif (comme le serveur)
  const projet = String(form.projectId ?? '').trim();
  if (form.autoFacturation && !projet) err('projectId', MSG_PROJET_REQUIS);
  else if (form.autoFacturation && Array.isArray(projets) && !projets.some((p) => p && p.id === projet)) err('projectId', MSG_PROJET_INACTIF);
  // Stockage sur dalle (22-10-2026) : comme le serveur (controlerStockage) — un projet CLIENT actif, les trois
  // réglages valides ; une date CHOISIE maintenant va du 1er du mois précédent à dans un an, sans mois clôturé entre
  // elle et aujourd'hui.
  if (form.stockageActif) {
    const p = Array.isArray(projets) ? projets.find((x) => x && x.id === projet) : undefined;
    if (!projet) err('stockage', MSG_STOCKAGE_PROJET);
    else if (Array.isArray(projets) && !p) err('stockage', MSG_PROJET_INACTIF);
    else if (p && p.kind && p.kind !== 'CLIENT') err('stockage', MSG_STOCKAGE_PROJET_CLIENT);
    const f = joursEnEntier(form.stockageFranchise);
    if (f === null || Number.isNaN(f) || f > MAX_FRANCHISE_JOURS) err('stockageFranchise', MSG_STOCKAGE_FRANCHISE);
    const c = prixEnCentimes(form.stockagePrix);
    if (c === null || Number.isNaN(c) || c < 1 || c > MAX_PRIX_CENTIMES) err('stockagePrix', MSG_STOCKAGE_PRIX);
    const j = String(form.stockageDepuis ?? '').trim();
    if (!jourValide(j)) err('stockageDepuis', MSG_STOCKAGE_DEPUIS);
    else if (j !== (form.stockageDepuisEnregistre || '')) {
      const { min, max } = bornesDepuis(aujourdhui);
      const clos = moisVerrouilles ? moisAVerifier(j, aujourdhui).find((m) => moisVerrouilles.has(m)) : null;
      if (j < min) err('stockageDepuis', `Stockage sur dalle — facturé à partir du : au plus tôt le ${texteJour(min)} (1er jour du mois précédent).`);
      else if (j > max) err('stockageDepuis', `Stockage sur dalle — facturé à partir du : au plus tard le ${texteJour(max)}.`);
      else if (clos) err('stockageDepuis', msgStockageMoisClos(clos));
    }
  }
  const enA = new Set(decouperEmails(form.readyTo).map((e) => e.toLowerCase()));
  const doubles = decouperEmails(form.readyCc).filter((e) => enA.has(e.toLowerCase()));
  if (doubles.length) avertissements.push(`${doubles.join(', ')} : déjà en À, inutile en copie.`);

  const langues = languesCochees(form);
  if (!langues.length) err('langues', MSG_AUCUNE_LANGUE);
  // Néerlandais coché : pas de modèle par défaut, sujet ET texte obligatoires (message d'abord).
  if (langues.includes('nl') && !modeleComplet(form.modeles.nl)) err('modeles.nl', MSG_NL_SANS_MODELE);
  if (form.loadMailEnabled && langues.includes('nl') && !modeleComplet(form.modelesCharge.nl)) err('modelesCharge.nl', MSG_NL_SANS_MODELE_CHARGE);
  // Tout modèle QUI PART est vérifié comme au serveur. Langue cochée : complet ou vide (un modèle
  // commencé est refusé). Langue décochée : son modèle ne part que s'il est complet (modelesAEnregistrer)
  // — alors il est vérifié aussi, avec de quoi le retrouver (son éditeur est caché).
  for (const l of LANGUES) {
    const coche = langues.includes(l);
    const sources = [['modeles', 'mail « commande prête »']];
    if (form.loadMailEnabled) sources.push(['modelesCharge', 'mail « chargée »']);
    for (const [cle, quoi] of sources) {
      const m = form[cle][l];
      if (!coche && !modeleComplet(m)) continue;
      const e = verifModele(m, l, quoi);
      if (e) err(`${cle}.${l}`, coche ? e : `${e} (langue décochée : cochez-la pour corriger ce modèle)`);
    }
  }
  const pl = (n, mot) => (n > 1 ? `${mot}s` : mot);
  for (const l of langues) {
    for (const [cle, connus, quoi] of [['modeles', REPERES, '« commande prête »'], ['modelesCharge', REPERES_CHARGE, '« chargée »']]) {
      if (cle === 'modelesCharge' && !form.loadMailEnabled) continue;
      const m = form[cle][l];
      if (!m) continue;
      const inconnus = reperesInconnus(`${m.subject}\n${m.body}`, connus);
      if (inconnus.length) avertissements.push(`${nomLangue(l)} ${quoi} : ${pl(inconnus.length, 'repère')} ${pl(inconnus.length, 'inconnu')} ${inconnus.join(', ')} — ${pl(inconnus.length, 'laissé')} tel${inconnus.length > 1 ? 's' : ''} quel${inconnus.length > 1 ? 's' : ''} dans le mail.`);
    }
  }
  return { erreurs, avertissements };
}
export const erreurDe = (v, champ) => (v.erreurs.find((e) => e.champ === champ) || {}).msg || null;

// Texte « modèle par défaut » montré quand une langue n'a pas de modèle personnalisé.
export const texteDefaut = (l) => (LANGUES_AVEC_DEFAUT.includes(l)
  ? 'Vide = modèle par défaut (le mail actuel, identique à aujourd\'hui)'
  : 'Pas de modèle par défaut en néerlandais : sujet et texte obligatoires');

export { NOMS_LANGUES };
